import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TextLibrary } from '../../../consts/defaultConfigValues/defaultConstants'
import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  CheckoutRuleDocumentType,
  CheckoutRules,
} from '../../../interfaces'
import { ICheckoutRuleValue } from '../../../graphQL/queries/getCheckoutRule/types'
import { IRule } from '../../../graphQL/queries/checkoutSessionRule/types'
import type { RootState } from '../../store'
import {
  AutoSettleStatus,
  CheckoutAutoSettlePayload,
  CheckoutAvailablePayload,
  CheckoutClearSelectionPayload,
  CheckoutLoadingPayload,
  CheckoutSelectionPayload,
  ICheckoutAvailable,
  ICheckoutRuleUiFlags,
  ICheckoutState,
} from './types'

/**
 * The three derived flags for one rule type, from the two facts that move
 * them.
 *
 * Pure and total: it answers for a rule type nothing has fetched, which is
 * what lets `initialState` seed every key before a screen exists.
 */
const deriveUiFlags = (
  available: ICheckoutAvailable | undefined,
  selection: ICheckoutRuleValue | undefined,
): ICheckoutRuleUiFlags => {
  /*
   * `loaded` is the load-bearing half. Testing only `data.length === 0` would
   * be true before the fetch lands, because an absent entry has no length —
   * and the shopper would read "no options" as the answer.
   *
   * Computed once and used for both flags, so the pair cannot drift into
   * disagreeing about the same state and leaving a screen with neither its
   * picker nor its message.
   */
  const offersNothing =
    available?.loadingState === 'loaded' && available.data.length === 0

  return {
    emptyHidden: !offersNothing,
    pickerHidden: offersNothing,
    errorHidden: available?.loadingState !== 'error',
    /*
     * Keyed on `data`, not on the key's presence. `setCheckoutRuleSession`
     * sends `value` as a JSON leaf and nothing server-side rejects an empty
     * `data`, so a selection carrying one would enable the CTA and write a
     * choice that is not a choice.
     */
    confirmDisabled: !selection?.data,
  }
}

/**
 * Rewrites one rule type's flags from the state around them.
 *
 * Called by every reducer that can move one. Recomputing beats patching: the
 * three flags read four facts between them, and a reducer that patched only
 * the flag it "obviously" affects would drift the first time a reducer touched
 * two of those facts at once.
 */
const recomputeUi = (
  state: ICheckoutState,
  ruleType: CheckoutRuleDocumentType,
) => {
  state.ui[ruleType] = deriveUiFlags(
    state.available[ruleType],
    state.selections[ruleType],
  )
}

/**
 * Whether any rule's auto-settle is still in flight.
 *
 * Recomputed across the whole map on every write rather than patched from the
 * status just set — the stance `recomputeUi` takes above, for the same reason.
 * The two rule types resolve independently, so a patch that read only the key
 * it moved would release the footer CTA while the other was still writing.
 *
 * Iterates `CHECKOUT_RULE_DOCUMENT_TYPES` rather than the map's own keys, so a
 * third rule document type fails the build at that array rather than being
 * silently left out of the hold.
 */
const deriveRulesResolving = (
  autoSettle: ICheckoutState['autoSettle'],
): boolean =>
  CHECKOUT_RULE_DOCUMENT_TYPES.some((ruleType) => {
    const status = autoSettle[ruleType]
    return status === 'resolving' || status === 'writing'
  })

/**
 * One all-quiet entry per rule type.
 *
 * The exhaustiveness guard lives on `CHECKOUT_RULE_DOCUMENT_TYPES` itself, so
 * a new union member fails the build there rather than silently seeding one
 * key fewer here. The cast is what that guard buys: an empty object is not yet
 * the full record, and there is no way to say so mid-construction.
 */
const seedUiFlags = (): ICheckoutState['ui'] => {
  const seeded = {} as ICheckoutState['ui']
  CHECKOUT_RULE_DOCUMENT_TYPES.forEach((ruleType) => {
    seeded[ruleType] = deriveUiFlags(undefined, undefined)
  })
  return seeded
}

export const initialState: ICheckoutState = {
  selections: {},
  available: {},
  ui: seedUiFlags(),
  lastAppliedRules: undefined,
  recoveryRedirects: [],
  autoSettle: {},
  rulesResolving: false,
  ctaLabel: TextLibrary.SUMMARY_CONTINUE_BUTTON,
}

export const checkoutSlice = createSlice({
  name: 'checkoutSlice',
  initialState,
  reducers: {
    /**
     * Records the shopper's pick for one rule type.
     *
     * Overwrites rather than merging, which mirrors the server:
     * `Session.setCheckoutRuleById` filters out the existing entry for the
     * type and appends, so a second pick replaces the first there too. A merge
     * here would let a stale `amount` survive a change of `data`.
     *
     * Writing this does **not** persist anything. The mutation is the writer;
     * this is the optimistic copy the picker renders from while it is in
     * flight, and it is discarded on reload.
     */
    setSelection: (
      state,
      { payload }: PayloadAction<CheckoutSelectionPayload>,
    ) => {
      state.selections[payload.ruleType] = payload.value
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Forgets the shopper's pick for one rule type.
     *
     * The rollback half of an optimistic write, and the reason it exists:
     * `setSelection` overwrites, so it can restore a previous pick but cannot
     * express "there was no pick". A failed `setCheckoutRuleSession` on a
     * shopper's first choice needs exactly that.
     *
     * Deleting the key rather than writing a blank `ICheckoutRuleValue` keeps
     * `selectSelectionByRuleType` honest — its `undefined` means "nothing
     * chosen", and a `{ data: '', amount: 0 }` sentinel would have to be
     * unpicked at every read.
     *
     * Leaves `available` alone. What the storefront offers did not change
     * because a write failed.
     */
    clearSelection: (
      state,
      { payload }: PayloadAction<CheckoutClearSelectionPayload>,
    ) => {
      delete state.selections[payload.ruleType]
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Stores a completed fetch and marks it `loaded`.
     *
     * `loaded` is set here rather than left to a separate `setLoadingState`
     * call, so no render can observe options that have arrived under a
     * `loading` flag. An empty `data` is a legitimate result and still reads
     * `loaded` — that is the pilot's answer for `PAYMENT_METHODS` today.
     */
    setAvailable: (
      state,
      { payload }: PayloadAction<CheckoutAvailablePayload>,
    ) => {
      state.available[payload.ruleType] = {
        data: payload.data,
        loadingState: 'loaded',
      }
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Moves one rule type's fetch between states without touching its data.
     *
     * Keeps whatever was already loaded, so a failed refetch shows the
     * previous options beside its error rather than blanking the screen. The
     * `orders` slice takes the same posture on a failed load-more.
     *
     * `error` is cleared unless this call carries one, so recovering from a
     * failure leaves nothing stale behind.
     */
    setLoadingState: (
      state,
      { payload }: PayloadAction<CheckoutLoadingPayload>,
    ) => {
      const existing: ICheckoutAvailable = state.available[payload.ruleType] ?? {
        data: [],
        loadingState: 'idle',
      }

      state.available[payload.ruleType] = {
        ...existing,
        loadingState: payload.loadingState,
        error: payload.error,
      }
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Stores the rules from the last `checkoutSessionRule` response.
     *
     * `useCheckoutRulePreflight` is the writer and `useCheckoutRuleRecovery`
     * the reader. COM-947 shipped the field with neither.
     *
     * Replaces rather than merges. The response is the engine's whole current
     * answer for this session, so a rule missing from it is a rule the engine
     * no longer names — merging would keep reporting a rule that has since
     * been satisfied.
     */
    setLastAppliedRules: (state, { payload }: PayloadAction<IRule[]>) => {
      state.lastAppliedRules = payload
    },
    /**
     * Records that the cart-entry pre-flight has sent the shopper to one
     * rule's picker, so it will not send them there again.
     *
     * Idempotent, because the caller dispatches it on a branch it can re-enter:
     * the pre-flight refires on every cart mount, and a shopper who chose
     * nothing still reads as unmet. Appending blindly would grow the list
     * without bound across a long session.
     *
     * ⚠️ **This is the only thing standing between the shopper and a redirect
     * loop they cannot leave.** See `recoveryRedirects` in `./types` for the
     * mechanism. Dropping the dispatch leaves every test green — the latch's
     * kill case in `hooks/checkout/tests/useCheckoutRulePreflight.test.tsx` is
     * what fails.
     */
    markRecoveryRedirect: (state, { payload }: PayloadAction<CheckoutRules>) => {
      if (state.recoveryRedirects.includes(payload)) return
      state.recoveryRedirects.push(payload)
    },
    /**
     * Records how the one-option auto-settle decided for one rule type.
     *
     * The only writer of `autoSettle`, and the only thing that moves
     * `rulesResolving`. Nothing dispatches it on this commit;
     * `useCheckoutRuleAutoSettle` arrives in PR 4 of this series and will
     * dispatch it once per transition: `'resolving'`, then `'writing'`, then a
     * terminal value.
     *
     * ⚠️ **A status that never reaches a terminal value disables checkout
     * for the rest of the session**, once PR 2 of this series has the footer
     * CTA binding `rulesResolving`: the flag stays `true` and the button never
     * re-enables. That is why the hook PR 4 adds arms a deadline on the two
     * transient states rather than trusting the request to settle — Apollo's
     * `HttpLink` carries no timeout.
     */
    setAutoSettleStatus: (
      state,
      { payload }: PayloadAction<CheckoutAutoSettlePayload>,
    ) => {
      state.autoSettle[payload.ruleType] = payload.status
      state.rulesResolving = deriveRulesResolving(state.autoSettle)
    },
    /**
     * Swaps the summary CTA's caption.
     *
     * Takes a `{{texts.*}}` token rather than resolved copy — see `ctaLabel`
     * in `types.ts` for why the config language leaves no other option.
     *
     * The only writer is `useSummaryCheckoutCta`, which skips a dispatch that
     * would change nothing. ⚠️ That guard is a saved no-op, **not** loop
     * prevention — measured, and the hook's own comment carries the retraction.
     * Immer returns the original state when a reducer assigns an equal value,
     * and the caption is selected as a primitive, so the effect stops
     * re-running on its own.
     */
    setCtaLabel: (state, { payload }: PayloadAction<string>) => {
      state.ctaLabel = payload
    },
    /**
     * Records — or forgets — where the guided walk should end.
     *
     * One reducer with a nullable payload rather than a set/clear pair. The
     * field has exactly two meanings, and a pair invites a third caller to
     * clear it from somewhere that has no business deciding the walk is over.
     */
    setGuidedReturn: (state, { payload }: PayloadAction<string | undefined>) => {
      state.guidedReturnPath = payload
    },
    /**
     * Drops the shopper's picks, keeping what the storefront offers.
     *
     * The asymmetry is the point. Selections belong to a session and mean
     * nothing in the next one. The offered options belong to the storefront
     * and survive — re-fetching them on a session change would be a request
     * for an answer already held.
     *
     * `recoveryRedirects` goes with them, and for the same reason: the latch
     * answers "have we already sent this shopper to that picker *this
     * session*". Keeping it across a session change would leave the new
     * session's first cart entry silently un-redirected.
     *
     * `autoSettle` goes with them too, and `rulesResolving` has to be lowered
     * beside it. Clearing the map without recomputing the boolean would strand
     * the footer CTA disabled across a session change, with no rule in flight
     * to clear it.
     *
     * ⚠️ **Nothing dispatches this.** No slice in this repo reacts to a
     * session change, and `defaultSlice.sessionId` is written once from the
     * URL, so the only transition production performs is `undefined` → real
     * id. It exists for the screen that first needs it to call, not to be
     * wired to a watcher here.
     */
    resetSelectionsForNewSession: (state) => {
      state.selections = {}
      state.lastAppliedRules = undefined
      state.recoveryRedirects = []
      state.autoSettle = {}
      state.guidedReturnPath = undefined
      /*
       * `ctaLabel` is deliberately absent. Everything reset here is the
       * shopper's — their picks, what the engine said about them, where they
       * have been sent. The caption is the storefront's, resolved out of
       * `config.texts`, and a new session changes neither. Clearing it would
       * blank the button until the summary's next effect ran.
       */
      state.rulesResolving = deriveRulesResolving(state.autoSettle)
      CHECKOUT_RULE_DOCUMENT_TYPES.forEach((ruleType) => {
        recomputeUi(state, ruleType)
      })
    },
  },
})

export const {
  setSelection,
  clearSelection,
  setAvailable,
  setLoadingState,
  setLastAppliedRules,
  markRecoveryRedirect,
  setAutoSettleStatus,
  setCtaLabel,
  setGuidedReturn,
  resetSelectionsForNewSession,
} = checkoutSlice.actions

/**
 * The shopper's pick for one rule type, or `undefined`.
 *
 * Curried so a component names the rule type at module scope and passes the
 * result straight to `useAppSelector`, rather than building a new closure on
 * every render.
 *
 * `RootState` is imported as a **type**. `store.tsx` imports this module, so a
 * value import would close a runtime cycle; a type-only import is erased.
 */
export const selectSelectionByRuleType =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): ICheckoutRuleValue | undefined =>
    state.checkoutSlice.selections[ruleType]

/**
 * What the storefront offers for one rule type, or `undefined` when nothing
 * has asked.
 *
 * ⚠️ `undefined` and `{ data: [], loadingState: 'loaded' }` are different
 * answers. The first means no fetch has run; the second means the storefront
 * offers nothing. A caller that reads `?.data ?? []` erases the distinction
 * and renders an empty-state message over a screen that has not loaded.
 */
export const selectAvailableByRuleType =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): ICheckoutAvailable | undefined =>
    state.checkoutSlice.available[ruleType]

export const selectLastAppliedRules = (
  state: RootState,
): IRule[] | undefined => state.checkoutSlice.lastAppliedRules

/**
 * Rule types the cart-entry pre-flight has already redirected for.
 *
 * Uncurried, like `selectLastAppliedRules` above: there is one list for the
 * session rather than one per rule type, so there is no argument to close
 * over.
 */
export const selectRecoveryRedirects = (
  state: RootState,
): CheckoutRules[] => state.checkoutSlice.recoveryRedirects

/**
 * How the auto-settle decided for one rule type, or `undefined`.
 *
 * COM-1046's *Continuar* walk reads this rather than re-deriving the option
 * count. `utils/checkoutRuleSelection.ts` `selectFirstHeldRule` is that
 * reader, and the set below is what it implements: exclude `'resolving'`,
 * `'writing'`, `'settled'`, `'empty'` and `'unavailable'`. (A *walk*, not a
 * queue — `usePlaceOrderFlow` clears its queue on unmount and `EventManager`
 * mounts one per container, so the next step is derived at every hop.)
 *
 * `'failed'` **stays in**, so the shopper reaches the picker rather than a
 * `createOrder` rejection once COM-1081 enforces the rule. ⚠️ And it stays in
 * **regardless of whether the rule reads settled**, which is the half that is
 * easy to drop: a returning shopper carries a persisted pick, the auto-settle
 * writes today's only option, the write is rejected, and the rollback leaves
 * `selections` empty — so `selectRulePick` falls back to the session copy and
 * the rule reads settled on a value the storefront no longer offers.
 * `'failed'` is a positive claim that the stored value is wrong.
 *
 * ⚠️ `undefined` is not `'multiple'`. It means nothing decided — a rule the
 * storefront does not serve, or a screen that never mounted
 * `useCheckoutRuleAutoSettle`. Reading it as "the shopper must pick" would
 * hold the CTA at *Continuar* on a read-only storefront, with no screen to
 * send them to. No storefront authors `checkoutRuleRoutes` yet, so it is
 * still the answer for every rule on every storefront.
 */
export const selectAutoSettleStatus =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): AutoSettleStatus | undefined =>
    state.checkoutSlice.autoSettle[ruleType]

export default checkoutSlice.reducer
